import {
  LoadingOutlined,
  SwapOutlined,
  SwapRightOutlined,
} from '@ant-design/icons';
import { Collapse, Tooltip, Typography } from 'antd';
import { ReactComponent as CancelOrderIcon } from 'app/assets/img/cancel_order_icon.svg';
import { ReactComponent as CancelOrderIconDisable } from 'app/assets/img/cancel_order_icon_disable.svg';
import StyledButton from 'app/components/StyledButton';
import StyledPagination from 'app/components/StyledPagination';
import StyledTable from 'app/components/StyledTable';
import useTitleSize from 'app/components/StyledTable/useTitleSize';
import { Permission } from 'app/hooks/usePermission';
import Images from 'app/assets/img';
import { notify } from 'reapop';
import {
  I_Paginator,
  I_PayloadGetListOrder,
} from 'app/pages/Account/ListOrder/slice/types';
import { useGlobalFunction } from 'app/pages/App/hooks/useGlobalFunction';
import {
  selectInfoUser,
  selectTranslations,
} from 'app/pages/App/slice/selectors';
import TooltipParagraph from 'app/pages/Catalog/tooltipParagraph';
import NodataView from 'app/pages/NewAccount/components/NodataView';
import moment from 'moment';
import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { objectType } from 'types';
import { formatNumber } from 'utils/helper';
import useCountDown from 'utils/hooks/useCountDown';
import { parseSecondsToMinutesAndHours } from 'utils/time';
import { isArray } from 'lodash-es';
import { TAB_ORDER_MANAGEMENT } from '.';
import { StyledStack } from 'app/components/Stack';
import {
  cancelOrderComboHolidayApi,
  checkoutPaymentOrderComboHolidayApi,
} from 'app/services/orderApi';
import { store } from 'index';
import _ from 'lodash';

export interface Order {
  subOrderState: string;
  id: number;
  totalPrice: string;
  totalTicket: number;
  paymentStatus: string;
  idHash: string;
  ticketChannel: string;
  orderType: string;
  holidaySubmitNumber: string;
  sapSubmitNumber: string | null;
  thirdPartySubmitNumber: string;
  orderDate: string;
  expiredAt: string;
  orderState: OrderState;
  invoiceStatus: string;
  seq: string;
  businessPartner: BusinessPartner;
  agent: Agent;
  payment: Payment;
  items: Item[];
}

export interface OrderState {
  value: string;
  name: string;
}

export interface BusinessPartner {
  id: number;
  name: string;
  fullName: string;
  code: string;
  phone: string;
  bpLongTerm: string;
  allowBuyShortTerm: boolean;
  allowBuyPaperTicket: boolean;
  allowReturnWithoutPolicy: boolean;
}

export interface Agent {
  id: number;
  username: string;
  name: string;
  address: string | null;
  phone: string;
  gender: string;
}

export interface Payment {
  id: number;
  code: string;
  name: string;
  description: string | null;
  mapVGS: string;
  validFrom: string;
  validTo: string;
  weight: number;
  default: string[];
}

export interface Item {
  id: number;
  productName: string;
  productCode: string;
  flights: Flight[];
  tickets: Ticket[];
}

export interface Flight {
  flightId: string;
  flightNumber: string;
  departure: string;
  arrival: string;
  departureTime: string;
  arrivalTime: string;
}

export interface Ticket {
  ticketId: string;
  name: string;
  quantity: number;
  siteCode: string;
  otaProductCode: string;
  adultQuantity: number;
  childQuantity: number;
  infantQuantity: number;
  usingDate: string;
  usingDateTo: string;
  park: Park;
}

export interface Park {
  id: number;
  name: string;
  workstationId: string;
}

function getTextWidth(text: string, font = '14px Inter') {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (!context) return 0;

  context.font = font;
  return context.measureText(text).width;
}

export type ListOrderLayoutProps = {
  loading: boolean;
  listOrder: any[];
  paginator?: I_Paginator;
  search?: (payload: Partial<I_PayloadGetListOrder>) => void;
  getCurrentSearchOpt?: () => I_PayloadGetListOrder;
  orderState: TAB_ORDER_MANAGEMENT;
  getListOrderOnCancelOrderSuccess?: () => void;
  setIsLoading?: Dispatch<SetStateAction<boolean>>;
  activeKeyTab?: string;
};

const getNodataText = (
  orderState: TAB_ORDER_MANAGEMENT,
  translations: objectType | undefined,
) => {
  switch (orderState) {
    case TAB_ORDER_MANAGEMENT.THANH_CONG:
      return translations ? translations['b2c.txt.nodataSuccess'] : '';
    case TAB_ORDER_MANAGEMENT.CHO_THANH_TOAN:
      return translations ? translations['b2c.txt.nodataPaymentPending'] : '';
    case TAB_ORDER_MANAGEMENT.DA_HUY:
      return translations ? translations['b2c.txt.nodataCancelled'] : '';
    default:
      break;
  }
};

const FormatDateCellWithLocation = ({ dateTimeValue }: any) => {
  const { locale } = useGlobalFunction();
  const formatStrDate =
    locale === 'en' ? 'HH:mm YYYY/MM/DD' : 'HH:mm DD/MM/YYYY';
  return <Typography>{dateTimeValue.format(formatStrDate)}</Typography>;
};

const RouteFormat = ({ flights }: any) => {
  const { locale } = useGlobalFunction();
  if (!flights) return <></>;
  else
    return (
      <div className="text-start">
        <div className="text-nowrap d-flex align-items-center gap-1">
          {flights?.[0]?.departure}
          {flights?.length === 1 ? (
            <SwapRightOutlined style={{ fontSize: 15 }} />
          ) : (
            <SwapOutlined />
          )}
          {flights?.[0]?.arrival}
        </div>
        <div className="text-nowrap" style={{ fontSize: '12px' }}>
          {flights?.length === 1
            ? locale === 'vi'
              ? 'Một chiều'
              : 'One way'
            : locale === 'vi'
            ? 'Khứ hồi'
            : 'Roundtrip'}
        </div>
      </div>
    );
};

const ListOrderLayout = ({
  listOrder,
  paginator,
  search,
  orderState,
  getCurrentSearchOpt,
  getListOrderOnCancelOrderSuccess,
  setIsLoading,
  activeKeyTab,
}: ListOrderLayoutProps) => {
  const formatString = 'HH:mm:ss DD/MM/YYYY';
  const translations = useSelector(selectTranslations);
  const infoUser = useSelector(selectInfoUser);
  const history = useHistory();

  const onClickShowDetail = (id: string) => {
    history.push(`/holiday/order-detail/${id}`, {
      prevPage: '/order-management',
      targetTab: activeKeyTab,
    });
  };

  const onCancelOrderSuccess = () => {
    search?.(getCurrentSearchOpt?.() || {});
  };

  const titleColumnSizes = useTitleSize({
    title: [
      'b2b.orderManagement.date-order',
      'b2b.orderManagement.date-order-code',
      'b2b.orderManagement.date-order-sap',
      'b2b.txt.comboType',
      'b2b.orderManagement.route',
      'b2b.orderManagement.fromDate',
      'b2b.orderManagement.toDate',
      'b2b.txt.hotel',
      'b2b.orderManagement.park',
      'b2b.orderManagement.ticketAmount',
      'b2b.txt.orderValue',
      'b2b.txt.totalDiscount',
      'b2b.orderManagement.ticketRecipient',
      'b2b.orderManagement.pttt',
      'b2c.txt.pttt',
      'b2b.txt.total-amount',
      'b2b.orderManagement.orderCode',
      'b2b.orderManagement.holdingTime',
      'b2b.orderManagement.action',
      'b2c.txt.total-amount',
      'b2b.txt.canceller',
      'b2b.txt.source',
      'b2b.txt.ticketStatus',
    ],
  });
  const columns = useMemo(
    () =>
      [
        {
          title: (
            <div style={{ textAlign: 'left', marginLeft: 20 }}>
              {translations?.['b2b.orderManagement.date-order'] ??
                'Ngày đặt đơn'}
            </div>
          ),
          dataIndex: 'orderDate',
          key: 'orderDate',
          render: orderDate => {
            const newDate = moment(orderDate, formatString);
            if (newDate.toString() === 'Invalid date')
              return (
                <div
                  style={{
                    minWidth:
                      titleColumnSizes['b2b.orderManagement.date-order'],
                  }}
                />
              );
            return (
              <div
                style={{
                  textAlign: 'left',
                  marginLeft: 20,
                  minWidth: titleColumnSizes['b2b.orderManagement.date-order'],
                }}
              >
                <Typography>{newDate.format('DD/MM/YYYY')}</Typography>
                <Typography>{newDate.format('HH:mm:ss')}</Typography>
              </div>
            );
          },
        },
        {
          title:
            translations?.['b2b.orderManagement.date-order-code'] ??
            'Mã đặt đơn',
          key: 'orderInfo.orderId',
          render: ticket => (
            <div
              style={{
                color: '#F8B117',
                cursor: 'pointer',
                minWidth:
                  titleColumnSizes['b2b.orderManagement.date-order-code'],
              }}
              onClick={() => {
                onClickShowDetail(ticket?.thirdPartySubmitNumber);
              }}
            >
              {ticket?.id}
            </div>
          ),
        },
        {
          title: translations?.['b2b.orderManagement.date-order-sap'] ?? '',
          key: 'sapSubmitNumber',
          render: ticket => (
            <div
              style={{
                minWidth:
                  titleColumnSizes['b2b.orderManagement.date-order-sap'],
              }}
            >
              {ticket.thirdPartySubmitNumber ?? ''}
            </div>
          ),
        },
        {
          title: translations?.['b2b.txt.comboType'] ?? '',
          key: 'comboTypeName',
          render: ticket => (
            <div
              style={{
                minWidth: titleColumnSizes['b2b.txt.comboType'],
              }}
            >
              {ticket?.comboType?.name ?? ''}
            </div>
          ),
        },
        activeKeyTab === TAB_ORDER_MANAGEMENT.CHO_THANH_TOAN && {
          title: (
            <div style={{ textAlign: 'left', marginLeft: 20 }}>
              {translations?.['b2b.orderManagement.holdingTime'] ??
                'Thời gian giữ đơn'}
            </div>
          ),
          key: 'orderDate',
          render: tickets => {
            // const onCancelOrder = async () => {
            //   if (tickets?.orderState?.value !== 'paymentPending') {
            //     return;
            //   }
            //   try {
            //     const formData = {
            //       idHash: tickets?.idHash?.trim(),
            //     };
            //     const response = await cancelOrderComboHolidayApi(formData);
            //     if (response?.status === 200) {
            //       onCancelOrderSuccess();
            //     }
            //   } catch (err: any) {
            //     onCancelOrderSuccess();
            //   }
            // };
            const onGetListOrder = async () => {
              if (tickets?.orderState?.value !== 'paymentPending') {
                return;
              }
              search?.(getCurrentSearchOpt?.() || {});
            };
            return (
              <div
                style={{
                  textAlign: 'left',
                  marginLeft: 20,
                  minWidth: titleColumnSizes['b2b.orderManagement.holdingTime'],
                }}
              >
                {tickets.expiredAt && (
                  <Countdown
                    onTimeout={onGetListOrder}
                    orderCheckout={tickets}
                    noText={true}
                  />
                )}
              </div>
            );
          },
        },
        {
          title: translations?.['b2b.orderManagement.route'] ?? 'Hành trình',
          key: 'orderInfo.sapSubmitNumber',
          render: ticket => (
            <div
              style={{
                minWidth: titleColumnSizes['b2b.orderManagement.route'],
              }}
            >
              {ticket?.items?.[0]?.flights &&
                ticket?.items?.[0]?.flights?.length > 0 && (
                  <RouteFormat flights={ticket?.items?.[0]?.flights} />
                )}
            </div>
          ),
        },
        {
          title: translations?.['b2b.orderManagement.fromDate'] ?? 'Ngày đi',
          key: 'orderInfo.sapSubmitNumber',
          render: ticket => {
            const newDate = moment(
              ticket?.items?.[0]?.flights?.[0]?.departureTime,
              formatString,
            );
            if (newDate.toString() === 'Invalid date')
              return (
                <div
                  style={{
                    minWidth: titleColumnSizes['b2b.orderManagement.fromDate'],
                  }}
                />
              );
            return (
              <div
                style={{
                  textAlign: 'left',
                  marginLeft: 20,
                  minWidth: 140,
                }}
              >
                <FormatDateCellWithLocation dateTimeValue={newDate} />
              </div>
            );
          },
        },
        {
          title: translations?.['b2b.orderManagement.toDate'] ?? 'Ngày về',
          key: 'orderInfo.sapSubmitNumber',
          render: ticket => {
            const newDate = moment(
              ticket?.items?.[0]?.flights?.[1]?.departureTime,
              formatString,
            );
            if (newDate.toString() === 'Invalid date')
              return (
                <div
                  style={{
                    minWidth: titleColumnSizes['b2b.orderManagement.toDate'],
                  }}
                />
              );
            return (
              <div
                style={{
                  textAlign: 'left',
                  marginLeft: 20,
                  minWidth: 140,
                }}
              >
                <FormatDateCellWithLocation dateTimeValue={newDate} />
              </div>
            );
          },
        },
        {
          title: translations?.['b2b.txt.hotel'] ?? '',
          key: 'hotelName',
          render: ticket => (
            <div
              style={{
                minWidth: titleColumnSizes['b2b.txt.hotel'],
              }}
            >
              {ticket?.items?.[0]?.hotels
                ?.map(hotel => hotel?.hotelName)
                .filter(Boolean)
                .join(', ') ?? ''}
            </div>
          ),
        },
        {
          // TODO: translation
          title: translations?.['b2b.orderManagement.park'] ?? 'Công viên',
          key: 'park',
          render: ticket => {
            if (
              !isArray(ticket?.items?.[0]?.tickets) ||
              !ticket?.items?.[0]?.tickets?.length
            ) {
              return (
                <div
                  style={{
                    minWidth: titleColumnSizes['b2b.orderManagement.park'],
                  }}
                />
              );
            }
            const lines =
              ticket?.items?.[0]?.tickets?.map(
                tick => tick?.park?.name || tick?.park || '',
              ) ?? [];

            const longestWidth = Math.max(
              ...lines.map(line => getTextWidth(line, '14px Inter')),
            );
            return (
              <div
                style={{
                  textAlign: 'left',
                  marginLeft: 20,
                  minWidth: longestWidth + 25,
                }}
              >
                {[
                  ...new Set(
                    ticket?.items?.[0]?.tickets?.map(
                      tick => tick?.park?.name || tick?.park,
                    ),
                  ),
                ].join('\n ')}
              </div>
            );
          },
        },
        {
          title:
            translations?.['b2b.orderManagement.ticketAmount'] ?? 'Số lượng',
          key: 'totalTicket',
          render: ticket => {
            const displayValue = ticket?.totalTicket;
            return (
              <div
                style={{
                  minWidth:
                    titleColumnSizes['b2b.orderManagement.ticketAmount'],
                }}
              >
                {formatNumber(displayValue, '.')}
              </div>
            );
          },
        },
        {
          title: translations?.['b2b.txt.orderValue'],
          key: 'totalAmount',
          render: ticket => {
            const displayValue = ticket?.totalPriceHoliday;
            return (
              <div
                style={{
                  textAlign: 'right',
                  minWidth: titleColumnSizes['b2b.txt.orderValue'],
                }}
              >
                {formatNumber(displayValue, '.') + 'đ'}
              </div>
            );
          },
        },
        {
          title: translations?.['b2b.txt.totalDiscount'],
          key: 'discount',
          render: ticket => {
            const displayValue = ticket?.discountCombo;
            return (
              <div
                style={{
                  textAlign: 'right',
                  minWidth: titleColumnSizes['b2b.txt.totalDiscount'],
                }}
              >
                {formatNumber(displayValue, '.') + 'đ'}
              </div>
            );
          },
        },
        {
          title: translations?.['b2c.txt.total-amount'],
          key: 'totalPayment',
          render: orderInfo => {
            const displayValue = orderInfo?.totalPrice;
            const prefix =
              orderInfo?.orderType === 'return' &&
              Number(displayValue) > 0 &&
              orderInfo?.orderState?.value === 'success'
                ? '-'
                : '';
            return (
              <div
                style={{
                  textAlign: 'right',
                  minWidth: titleColumnSizes['b2c.txt.total-amount'],
                }}
              >
                {prefix + formatNumber(displayValue, '.') + 'đ'}
              </div>
            );
          },
        },
        infoUser?.role_code !== Permission.ROLE_SALER && {
          title: (
            <div style={{ textAlign: 'center' }}>
              {translations?.['b2b.orderManagement.ticketRecipient'] ??
                'Người đặt đơn'}
            </div>
          ),
          render: value => (
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                minWidth:
                  titleColumnSizes['b2b.orderManagement.ticketRecipient'],
              }}
            >
              <TooltipParagraph
                ellipsis={{ rows: 1 }}
                style={{
                  marginBottom: 0,
                  maxWidth: 164,
                  textAlign: 'center',
                }}
              >
                {value?.agent?.name}
              </TooltipParagraph>
            </div>
          ),
        },
        activeKeyTab === TAB_ORDER_MANAGEMENT.DA_HUY && {
          title: translations?.['b2b.txt.canceller'] ?? 'Người hủy',
          key: 'orderInfo.canceller',
          render: orderInfo => (
            <div
              style={{
                minWidth: titleColumnSizes['b2b.txt.canceller'],
              }}
            >
              {orderInfo?.canceller}
            </div>
          ),
        },
        {
          title: (
            <div style={{ textAlign: 'center' }}>
              {translations?.['b2b.orderManagement.pttt'] ??
                'Phương thức thanh toán'}
            </div>
          ),
          key: 'paymentName',
          // render: value => value?.agent?.name,
          render: value => (
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                minWidth: titleColumnSizes['b2b.orderManagement.pttt'],
              }}
            >
              <TooltipParagraph
                ellipsis={{ rows: 1 }}
                style={{
                  marginBottom: 0,
                  maxWidth: 164,
                  textAlign: 'center',
                }}
              >
                {value?.payment?.name}
              </TooltipParagraph>
            </div>
          ),
        },
        activeKeyTab === TAB_ORDER_MANAGEMENT.THANH_CONG && {
          title: translations?.['b2b.txt.source'] ?? 'Nguồn',
          key: 'orderInfo.source',
          render: orderInfo => (
            <div
              style={{
                minWidth: titleColumnSizes['b2b.txt.source'],
              }}
            >
              {orderInfo?.orderSourceName}
            </div>
          ),
        },
        activeKeyTab === TAB_ORDER_MANAGEMENT.THANH_CONG && {
          title:
            translations?.['b2b.orderManagement.orderCode'] ?? 'Mã hóa đơn',
          key: 'orderInfo.seq',
          render: orderInfo => (
            <div
              style={{
                minWidth: titleColumnSizes['b2b.orderManagement.orderCode'],
              }}
            >
              {orderInfo?.seq}
            </div>
          ),
        },
        activeKeyTab === TAB_ORDER_MANAGEMENT.CHO_THANH_TOAN && {
          title: translations?.['b2b.orderManagement.action'] ?? 'Thao tác',
          key: 'action',
          render: orderInfo => {
            const isAllowPay = _.isEmpty(orderInfo?.subOrderState)
              ? true
              : false;
            return (
              <div
                style={{
                  minWidth: titleColumnSizes['b2b.orderManagement.action'],
                }}
              >
                <StyledStack columnGap={'12px'} justifyContent={'center'}>
                  <Tooltip
                    color="white"
                    title={translations?.['b2b.txt.pay-now']}
                    overlayInnerStyle={{ color: 'black' }}
                  >
                    <StyledButton
                      onClick={() => {}}
                      // disabled={!orderInfo?.expiredAt || moment() >= moment(
                      //   orderInfo?.expiredAt ?? undefined,
                      //   'HH:mm:ss DD/MM/YYYY',
                      // )}
                      bgColor="white"
                      style={{
                        width: 30,
                        height: 30,
                        justifyContent: 'center',
                        alignItems: 'center',
                        display: 'flex',
                        cursor: 'pointer',
                      }}
                    >
                      <CheckoutaymentOrderComponent
                        idHash={orderInfo?.idHash}
                        onCheckoutSuccess={onCancelOrderSuccess}
                        isDisable={
                          !isAllowPay ||
                          orderInfo?.subOrderState === 'pendingConfirmation' ||
                          orderInfo?.agent?.id !== infoUser?.id
                        }
                      />
                    </StyledButton>
                  </Tooltip>
                  <Tooltip
                    color="white"
                    title={translations?.['b2b.txt.cancel-order']}
                    overlayInnerStyle={{ color: 'black' }}
                  >
                    <StyledButton
                      onClick={() => {}}
                      bgColor="white"
                      style={{
                        width: 30,
                        height: 30,
                        justifyContent: 'center',
                        alignItems: 'center',
                        display: 'flex',
                        cursor: 'pointer',
                      }}
                    >
                      <CancelOrderComponent
                        idHash={orderInfo?.idHash}
                        onCancelOrderSuccess={onCancelOrderSuccess}
                      />
                    </StyledButton>
                  </Tooltip>
                </StyledStack>
              </div>
            );
          },
        },
        activeKeyTab === TAB_ORDER_MANAGEMENT.THANH_CONG && {
          title: translations?.['b2b.txt.ticketStatus'] ?? 'Trạng thái xuất vé',
          key: 'issueTicket',
          render: ticket => (
            <div
              style={{
                minWidth: titleColumnSizes['b2b.txt.ticketStatus'],
              }}
            >
              {ticket.issueTicket ?? ''}
            </div>
          ),
        },
      ].filter(item => typeof item !== 'boolean'),
    [
      translations,
      activeKeyTab,
      infoUser?.role_code,
      titleColumnSizes,
      onClickShowDetail,
      onCancelOrderSuccess,
    ],
  );

  const tableRef = useRef<any>();

  return (
    <>
      {listOrder.length > 0 ? (
        <div style={{ marginTop: 10 }}>
          <StyledTable
            tableRef={tableRef}
            dataSource={listOrder}
            columns={columns as object[]}
            pagination={false}
            tableProps={{
              rowKey: (record, index) => `${(record as Order).idHash}${index}`,
              scroll: {
                y: '50vh',
                x: 'auto',
              },
            }}
            styleSlots={{
              antTableContainer: {
                overflow: 'hidden',
              },
              antTableHeader: {
                border: 'none !important',
                borderRadius: '0 !important',
              },
              tableCSS: {
                borderTopLeftRadius: '0 !important',
                borderTopRightRadius: '0 !important',
              },
              antTableBody: {
                paddingBottom: 10,
              },
            }}
            // tableProps={rowKey:(record, index) => `${(record as Order)._id}${index}`}
          />
          {paginator && (
            <>
              {/* <StyledGradientDivider style={{ marginTop: '40px' }} /> */}
              <StyledPagination
                style={{
                  marginTop: '40px',
                }}
                showSizeChanger
                // defaultCurrent={1}
                current={paginator.current}
                total={paginator.totalCount} // Total number of data items
                pageSize={paginator.numItemsPerPage} // Number of items per page
                onChange={(page, pageSize) => {
                  window.scrollTo(0, 0);
                  search && search({ page, limit: pageSize });
                  tableRef?.current
                    ?.querySelector('.ant-table-body')
                    ?.scrollTo({
                      top: 0,
                      behavior: 'smooth',
                    });
                }} // Function to call when the page changes
                pageSizeOptions={['20', '50', '100']}
              />
            </>
          )}
        </div>
      ) : (
        <NodataView
          text={getNodataText(orderState, translations)}
          hideBtn={true}
        />
      )}
    </>
  );
};

export const StyledCollapse = styled(Collapse)`
  border: none;
  background-color: #ffffff;
  .ticket-info-title {
    margin-bottom: 0px !important;
  }
  .ant-collapse-expand-icon {
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .ant-collapse-header {
    background-color: #ffffff;
    margin-bottom: 24px;
  }
  .ant-collapse-item {
    border: none;
  }
  .ant-collapse-header {
    padding: 0 !important;
  }
  .ant-collapse-content {
    border: none;
  }
  .ant-collapse-content > .ant-collapse-content-box {
    padding: 0 !important;
  }
`;

export const Countdown = ({
  onTimeout,
  orderCheckout,
  noText,
}: {
  onTimeout?: () => void;
  orderCheckout?: Order;
  noText?: boolean;
}) => {
  const isTimeout = useRef(false);
  const translations = useSelector(selectTranslations);
  const expiredAt = moment(
    orderCheckout?.expiredAt ?? undefined,
    'HH:mm:ss DD/MM/YYYY',
  );
  const startTime = moment();
  const time = expiredAt.valueOf() - startTime.valueOf();
  const { timeRemaining } = useCountDown({
    time: time < 0 ? 0 : time,
    interval: 1000,
    startTime: startTime.valueOf(),
  });
  const { minutes, seconds, hours } = parseSecondsToMinutesAndHours(
    timeRemaining / 1000,
  );

  useEffect(() => {
    if (timeRemaining <= 0 && !isTimeout.current) {
      onTimeout && onTimeout();
      isTimeout.current = true;
    }
  }, [timeRemaining, onTimeout]);

  useEffect(() => {
    return () => {
      isTimeout.current = true;
    };
  }, []);
  const antIcon = useMemo(
    () => <LoadingOutlined style={{ fontSize: 16, color: '#E92127' }} spin />,
    [],
  );
  return (
    <div>
      {!noText && translations
        ? translations['b2b.orderManagement.holdingTime']
        : ''}{' '}
      {timeRemaining === 48 * 60 * 60 * 1000 ? (
        antIcon
      ) : (
        <span style={{ color: '#E92127' }}>{`${hours < 10 ? '0' : ''}${hours}:${
          minutes < 10 ? '0' : ''
        }${minutes}:${seconds < 10 ? '0' : ''}${seconds}`}</span>
      )}
    </div>
  );
};

export default ListOrderLayout;

export const CancelOrderComponent = ({
  idHash,
  onCancelOrderSuccess,
  isDisable,
}: {
  idHash: string;
  onCancelOrderSuccess?: () => void;
  isDisable?: boolean;
}) => {
  const translations = useSelector(selectTranslations);
  const { onOpenDialogNoti, onCloseDialogNoti } = useGlobalFunction();
  const [isRemoving, setIsRemoving] = useState(false);

  const antIcon = useMemo(
    () => <LoadingOutlined style={{ fontSize: 16, color: '#F8B117' }} spin />,
    [],
  );

  const onCancelOrder = async () => {
    try {
      setIsRemoving(true);
      onCloseDialogNoti();
      const formData = {
        idHash: idHash?.trim(),
      };
      const response = await cancelOrderComboHolidayApi(formData);
      if (response?.status === 200) {
        onCancelOrderSuccess?.();
        store.dispatch(
          notify(
            translations
              ? translations['b2b.orderManager.cancelSuccessfully']
              : 'Hủy đơn hàng thành công.',
            'success',
            {
              title: '',
              dismissAfter: 2000,
            },
          ),
        );
      }
    } catch (err: any) {
      onOpenDialogNoti({
        type: 'ERROR',
        isShow: true,
        title: '',
        content:
          err?.message ?? translations?.['b2b.txt.cancelOrderFailed'] ?? '',
        onCloseDialog: onCloseDialogNoti,
      });
    } finally {
      setIsRemoving(false);
    }
  };

  const handleClick = () => {
    if (isRemoving) return;

    onOpenDialogNoti({
      type: 'ERROR',
      isShow: true,
      title: '',
      content: translations?.['b2b.txt.cfRemoveOrder'] ?? '',
      closable: false,
      buttons: [
        {
          text: translations?.['b2b.txt.back'] ?? '',
          bg: '#ffff',
          border: 'solid 1px #808080',
          color: '#808080',
          onClick: () => {
            onCloseDialogNoti();
          },
        },
        {
          text: translations?.['b2b.txt.cancel-order'] ?? '',
          bg: 'linear-gradient(79.97deg, #F06F24 -7.79%, #F8B117 107.61%)',
          color: 'white',
          onClick: onCancelOrder,
        },
      ],
    });
  };

  return (
    <div
      className="d-flex align-items-center"
      style={{ cursor: isDisable ? 'not-allowed' : 'pointer' }}
      onClick={isDisable ? undefined : handleClick}
    >
      {isRemoving ? (
        antIcon
      ) : isDisable ? (
        <CancelOrderIconDisable />
      ) : (
        <CancelOrderIcon />
      )}
      {/* <span style={{ color: isDisable ? '#808080' : '#F8B117', marginLeft: 8 }}>
        {translations?.['b2b.txt.cancel-order'] ?? ''}
      </span> */}
    </div>
  );
};

export const CheckoutaymentOrderComponent = ({
  idHash,
  onCheckoutSuccess,
  isDisable,
}: {
  idHash: string;
  onCheckoutSuccess?: () => void;
  isDisable?: boolean;
}) => {
  const translations = useSelector(selectTranslations);
  const { onOpenDialogNoti, onCloseDialogNoti } = useGlobalFunction();
  const [isRemoving, setIsRemoving] = useState(false);

  const antIcon = useMemo(
    () => <LoadingOutlined style={{ fontSize: 16, color: '#F8B117' }} spin />,
    [],
  );

  const onConfirmpaymentOrder = async () => {
    try {
      setIsRemoving(true);
      onCloseDialogNoti();
      // const formData = {
      //   idHash: idHash?.trim(),
      // };
      const formData = new FormData();
      formData.append('idHash', idHash?.trim());
      formData.append('type', 'holiday');
      const response = await checkoutPaymentOrderComboHolidayApi(formData);
      if (response?.status === 200) {
        onCheckoutSuccess?.();
        store.dispatch(
          notify(
            translations
              ? translations['b2b.callBack.success']
              : 'Thanh toán thành công.',
            'success',
            {
              title: '',
              dismissAfter: 2000,
            },
          ),
        );
      }
    } catch (err: any) {
      onOpenDialogNoti({
        type: 'ERROR',
        isShow: true,
        title: '',
        content: err?.message ?? translations?.['b2b.callBack.fail'] ?? '',
        onCloseDialog: onCloseDialogNoti,
      });
    } finally {
      setIsRemoving(false);
    }
  };

  const handleClick = () => {
    if (isRemoving) return;

    onOpenDialogNoti({
      type: 'ERROR',
      isShow: true,
      title: '',
      content:
        translations?.['b2b.txt.cfCheckoutPayment'] ??
        'Thanh toán đơn hàng này?',
      closable: false,
      buttons: [
        {
          text: translations?.['b2b.txt.no'] ?? '',
          bg: '#ffff',
          border: 'solid 1px #808080',
          color: '#808080',
          onClick: () => {
            onCloseDialogNoti();
          },
        },
        {
          text: translations?.['b2b.txt.yes'] ?? 'Có',
          bg: 'linear-gradient(79.97deg, #F06F24 -7.79%, #F8B117 107.61%)',
          color: 'white',
          onClick: onConfirmpaymentOrder,
        },
      ],
    });
  };
  const disableStyle: React.CSSProperties = {
    pointerEvents: 'none',
    filter: 'contrast(0)',
    cursor: 'no-drop',
  };
  return (
    <div
      className="d-flex align-items-center"
      style={{ cursor: isDisable ? 'not-allowed' : 'pointer' }}
      onClick={isDisable ? undefined : handleClick}
    >
      {isRemoving ? (
        antIcon
      ) : isDisable ? (
        <img src={Images.PaymentIcon2} alt="" style={disableStyle} />
      ) : (
        <img src={Images.PaymentIcon2} alt="" style={{}} />
      )}
      {/* <span style={{ color: isDisable ? '#808080' : '#F8B117', marginLeft: 8 }}>
        {translations?.['b2b.txt.cancel-order'] ?? ''}
      </span> */}
    </div>
  );
};
